import { useDispatch, useSelector } from 'react-redux';
import PendingCard from '@/components/Dashboard/DashboardPendings/PendingsCard';
import GenericBarChart from '../../../Charts/GenericBarChart';
import { setPropertiesLocations } from '@/store/slices/components/dashboard/DashboardSlice';
import {
  MAP_OPERATIONS,
  PROPERTY_OPERATIONS,
} from '@/components/Dashboard/constants';
import {
  getHeaderUrl,
  getParsedLabels,
  handleClickBar,
  handleTooltip,
} from './utils';
import RadioButtonsGroup from '@/components/Dashboard/RadioButtonsGroup';
import useFetchDashboard from '@/components/Dashboard/customHooks/useFetchDashboard';
import { getOverviewPropertiesLocations } from '@/store/slices/components/dashboard/service';
import Trans from '@/components/shared/Trans';
import translations from '@/components/Dashboard/Dashboard.trans.json';
import PropertyIcon from '@/public/images/dashboard/property.svg';
import DashboardEmptyStateCard from '@/components/Dashboard/DashboardEmptyStateCard';
import { useTrackOverviewTab } from '@/components/Dashboard/customHooks/useTrackDashboard';

export default function PropertiesLocations() {
  const dispatch = useDispatch();
  const { overviewPropertiesLocations, miniCards } = useSelector(
    (state) => state.DashboardSlice
  );
  const { selectedIds } = useSelector((state) => state.FilterBarSlice.filters);
  const track = useTrackOverviewTab();
  const { ref, isStale, useFetchOnViewport } = useFetchDashboard();

  const { loading, hasFetched, labels, datasets, selectedOperation } =
    overviewPropertiesLocations;

  const operationId = MAP_OPERATIONS[selectedOperation];
  const { selectedStatusId: statusId, propertiesValue } = miniCards;

  useFetchOnViewport(
    (query) => {
      dispatch(
        getOverviewPropertiesLocations({ query, operationId, statusId })
      );
    },
    [statusId, operationId],
    { hasFetched }
  );

  const parsedLabels = getParsedLabels(labels, Trans(translations, 'Others'));
  const headerUrl = getHeaderUrl(operationId, statusId, selectedIds);

  const translateTooltip = (propsValue, locationsValue) =>
    Trans(translations, 'CountPropertiesInLocations', [
      propsValue,
      locationsValue,
    ]);

  const handleClickRadio = (operation) => {
    dispatch(
      setPropertiesLocations({
        key: 'selectedOperation',
        value: operation.id,
      })
    );
  };

  const allowedOptions = [
    { id: PROPERTY_OPERATIONS.SALE, text: Trans(translations, 'Sale') },
    { id: PROPERTY_OPERATIONS.RENT, text: Trans(translations, 'Rent') },
    {
      id: PROPERTY_OPERATIONS.TEMPORARY,
      text: Trans(translations, 'Temporary'),
    },
  ];

  return (
    <PendingCard ref={ref} loading={loading || isStale}>
      <PendingCard.Header
        redirectUrl={propertiesValue === 0 ? null : headerUrl}
        toolTipText={Trans(translations, 'ShowAll')}
        trackCallback={(element) => track.propertyLocation(element)}
      >
        {Trans(translations, 'Location')}
      </PendingCard.Header>
      {propertiesValue === 0 ? (
        <DashboardEmptyStateCard
          icon={PropertyIcon}
          title={Trans(
            translations,
            'HereYouWillSeeHowManyPropertiesYouHaveInEachLocation'
          )}
        />
      ) : (
        <>
          <RadioButtonsGroup
            onClickRadio={handleClickRadio}
            selectedOption={selectedOperation}
            allowedOptions={allowedOptions}
          />
          <GenericBarChart
            labels={parsedLabels}
            datasets={datasets}
            onBarClick={
              selectedIds.length > 1
                ? null
                : ({ label }) =>
                    handleClickBar(
                      operationId,
                      label.locationId,
                      statusId,
                      selectedIds
                    )
            }
            getTooltipLabel={(context) =>
              handleTooltip(context, labels, translateTooltip)
            }
          />
        </>
      )}
    </PendingCard>
  );
}
